import { createContext, useCallback, useContext } from 'react';
import {
  AssistantFrameCreateApi,
  AssistantFrameExtendedApi,
  AssistantFrameListApi,
  AssistantFrameUpdateApi,
} from './AgentFrameApiProviderTypes';
import useApiService from './ApiService';
import CONFIG from './CONFIG';

interface PromptApiContextType {
  getAgentFrames: (skip?: number, limit?: number) => Promise<AssistantFrameListApi>;
  getAgentFrameById: (agentFrameId: string) => Promise<AssistantFrameExtendedApi>;
  createAgentFrame: (data: AssistantFrameCreateApi) => Promise<AssistantFrameExtendedApi>;
  updateAgentFrame: (
    agentFrameId: string,
    data: AssistantFrameUpdateApi,
  ) => Promise<AssistantFrameExtendedApi>;
  deleteAgentFrame: (agentFrameId: string) => Promise<void>;
}

const AgentFrameApiContext = createContext<PromptApiContextType | null>(null);

export function AgentFrameApiProvider({ children }) {
  const { get, put, del } = useApiService(CONFIG.SERVER_ASSISTANT);

  const getAgentFrames = useCallback(
    async (skip: number = 0, limit: number = 10): Promise<AssistantFrameListApi> => {
      try {
        const response = await get(`/v1/assistant_frames?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching agent frames:', error);
        throw error;
      }
    },
    [get],
  );

  const getAgentFrameById = useCallback(
    async (agentFrameId: string): Promise<AssistantFrameExtendedApi> => {
      try {
        const response = await get(`/v1/assistant_frames/${agentFrameId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching agent frame by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createAgentFrame = useCallback(
    async (data: AssistantFrameCreateApi): Promise<AssistantFrameExtendedApi> => {
      try {
        const response = await put(`/v1/assistant_frames`, data);
        return response.data;
      } catch (error) {
        console.error('Error creating agent frame:', error);
        throw error;
      }
    },
    [put],
  );

  const updateAgentFrame = useCallback(
    async (
      agentFrameId: string,
      data: AssistantFrameUpdateApi,
    ): Promise<AssistantFrameExtendedApi> => {
      try {
        const response = await put(`/v1/assistant_frames/${agentFrameId}`, data);
        return response.data;
      } catch (error) {
        console.error('Error updating agent frame:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteAgentFrame = useCallback(
    async (agentFrameId: string): Promise<void> => {
      try {
        const response = await del(`/v1/assistant_frames/${agentFrameId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting agent frame:', error);
        throw error;
      }
    },
    [del],
  );

  return (
    <AgentFrameApiContext.Provider
      value={{
        getAgentFrames,
        getAgentFrameById,
        createAgentFrame,
        updateAgentFrame,
        deleteAgentFrame,
      }}
    >
      {children}
    </AgentFrameApiContext.Provider>
  );
}

export function useAgentFrameApi(): PromptApiContextType {
  const context = useContext(AgentFrameApiContext);
  if (!context) {
    throw new Error('useAgentFrameApi must be used within an AgentFrameApiProvider');
  }
  return context;
}
